import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { getRoleDetail, updateRolePermissions } from "services/permission";
import { verifyResponse } from "utils/httpRequest";
import { StyledRoleDetail } from "./Role.style";

const RoleDetail = ({ role, access, permissionByModule }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { register, handleSubmit, setValue, watch } = useForm();
  const [loading, setLoading] = useState(false);

  const loadRoleDetail = async () => {
    setLoading(true);
    try {
      const response = await getRoleDetail(role.id);
      verifyResponse(response);

      const permissionIdList = (response.data.permissionList || []).map(
        (permission) => permission.id
      );

      for (const module of permissionByModule || []) {
        for (const permission of module.permissionList || []) {
          setValue(
            `permission-${permission.id}`,
            permissionIdList.includes(permission.id)
          );
        }
      }
    } catch {
      enqueueSnackbar("Failed to load role permissions.", {
        variant: "warning",
      });
    } finally {
      setLoading(false);
    }
  };

  const changeModuleCheckedStatus = (module, status) => {
    const idList = (module.permissionList || []).map((permission) => permission.id);
    for (const permissionId of idList) {
      setValue(`permission-${permissionId}`, status);
    }
  };

  useEffect(() => {
    if (role && permissionByModule?.length) {
      loadRoleDetail();
    }
  }, [role?.id, permissionByModule.length]);

  const handleAction = async (data) => {
    const arr = Object.keys(data).map((key) => ({ key, value: data[key] }));
    const permissions = arr
      .filter((o) => o.value)
      .map((o) => parseInt(o.key.split("-")[1], 10));
    setLoading(true);
    try {
      const response = await updateRolePermissions({
        roleId: role.id,
        permissionIdList: permissions,
      });
      verifyResponse(response);
      if (response.rtnCode === 200) {
        enqueueSnackbar("Role permissions updated.", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Role permission update failed.", {
          variant: "warning",
        });
      }
    } catch {
      enqueueSnackbar("Role permission update failed.", {
        variant: "warning",
      });
    } finally {
      setLoading(false);
    }
  };

  const content =
    !loading && role && permissionByModule && access.getDetailByIdRoleAccess ? (
      <StyledRoleDetail>
        <Card className="role-summary-card">
          <CardContent>
            <div className="summary-grid">
              <div className="summary-item">
                <div className="summary-label">Role Name</div>
                <div className="summary-value">{role.name}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Role Description</div>
                <div className="summary-value">{role.description || "-"}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="permission-card">
          <CardContent>
            <form onSubmit={handleSubmit(handleAction)}>
              <div className="permission-header">
                <h3 className="permission-title">Permissions</h3>
                <div>
                  {access.setPermissionRoleAccess && (
                    <Button type="submit" variant="contained" size="small">
                      Save
                    </Button>
                  )}
                </div>
              </div>
              {(permissionByModule || []).map((moduleItem) => (
                <div className="module-section" key={`module-item-${moduleItem.module}`}>
                  <div className="module-header">
                    <p className="module-title">{moduleItem.module}</p>
                    <FormControlLabel
                      className="select-all-checkbox"
                      control={
                        <Checkbox
                          size="small"
                          onChange={(e) => {
                            changeModuleCheckedStatus(
                              moduleItem,
                              e.target.checked
                            );
                          }}
                        />
                      }
                      label="Select All"
                    />
                  </div>
                  <div className="permission-list ">
                    {(moduleItem.permissionList || []).map(
                      (permission) => (
                        <FormControlLabel
                          checked={!!watch(`permission-${permission.id}`)}
                          control={
                            <Checkbox
                              size="small"
                              onChange={(e) => {
                                setValue(
                                  `permission-${permission.id}`,
                                  e.target.checked
                                );
                              }}
                            />
                          }
                          label={permission.description}
                          key={`permission-item-${permission.id}`}
                          {...register(`permission-${permission.id}`)}
                        />
                      )
                    )}
                  </div>
                </div>
              ))}
            </form>
          </CardContent>
        </Card>
      </StyledRoleDetail>
    ) : (
      access.getDetailByIdRoleAccess && <div className="detail-loading">Loading role detail...</div>
    );

  return [content, loading];
};

export default RoleDetail;
